import * as Yup from "yup";
import {Input} from "../components/tailwind/input";
import {Button} from "../components/tailwind/button";
import {useAuth} from "../context/use-auth";
import {useForm} from "react-hook-form";
import {yupResolver} from "@hookform/resolvers/yup";
import {Heading, Subheading} from "../components/tailwind/heading.tsx";
import {useLocation, useNavigate} from "react-router-dom";
import {ProblemDetail} from "../types/problem-details.ts";
import ValidationAlert from "../components/validation-alert.tsx";
import BrandLogo from "../components/brand-logo.tsx";
import {useTranslation} from "react-i18next";
import {Text} from "../components/tailwind/text.tsx";
import {Divider} from "../components/tailwind/divider.tsx";
import {useCommonTranslation} from "../i18n/hooks";
import {useState, useEffect} from "react";
import {getSystemEmailStatus} from "../services/system-email-services";
import {Link} from "react-router-dom";

type LoginFormsInput = {
    username: string;
    password: string;
}

const validation = Yup.object().shape({
    username: Yup.string().required("errors.username-empty"),
    password: Yup.string().required("errors.password-empty")
});

export default function LoginPage() {

    const { t } = useTranslation('login')
    const { t: tCommon } = useCommonTranslation()
    const navigate = useNavigate()
    const location = useLocation()
    const redirectFrom = location.state?.from
    const redirectToAfterLogin = redirectFrom
        ? `${redirectFrom.pathname}${redirectFrom.search || ""}`
        : "/dashboard"
    const {loginUser} = useAuth();
    const [isLoading, setIsLoading] = useState(false)
    const [systemEmailConfigured, setSystemEmailConfigured] = useState(false)

    useEffect(() => {
        let cancelled = false
        getSystemEmailStatus()
            .then(status => {
                if (!cancelled) setSystemEmailConfigured(status.configured)
            })
            .catch(() => {
                if (!cancelled) setSystemEmailConfigured(false)
            })
        return () => {
            cancelled = true
        }
    }, [])
    const {
        register,
        handleSubmit,
        formState: {errors},
        setError
    } = useForm<LoginFormsInput>({resolver: yupResolver(validation), mode: "onChange"})

    const handleLogin = async (form: LoginFormsInput) => {
        setIsLoading(true)
        
        try {
            await loginUser(form.username, form.password)
            // Navigation erfolgt nach erfolgreichem Login
            navigate(redirectToAfterLogin)
        } catch (reason: any) {
            const error = reason as ProblemDetail
            if (error.validationIssues) {
                error.validationIssues.forEach(finding => {
                    setError(
                        finding.field as "username" | "password" | `root.${string}` | "root",
                        {
                            message: finding.message
                        }
                    )
                })
            } else if (error.status === 401) {
                setError("root", {
                    type: "401",
                    message: t("errors.login-failed")
                })
                setError("username", {})
                setError("password", {})
            } else {
                setError("root", {
                    type: `${error.status}`,
                    message: tCommon("serverError.responseCode", {statusCode: error.status})
                })
            }
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <div>
            <div className="mx-auto mb-10 flex max-w-4xl flex-col items-center gap-4">
                <BrandLogo className="size-28 sm:size-32"/>
                <Heading level={1} className="text-center">{tCommon("brand.name")}</Heading>
            </div>
            <form
                onSubmit={handleSubmit(handleLogin)}
                className="mx-auto max-w-4xl"
                data-testid="login-form"
                data-loading={isLoading}>
                <Heading level={2}>{t("headline")}</Heading>

                <Divider className="my-10 mt-6"/>

                {errors.root && (
                    <section role="alert" data-testid="login-error">
                        <ValidationAlert message={errors.root.message || t("errors.unknown-server-issue")}/>
                    </section>
                )}

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2">
                    <div className="space-y-1">
                        <Subheading>{t("form.username.subheading")}:</Subheading>
                        <Text>{t("form.username.text")}</Text>
                    </div>
                    <div className="flex flex-col justify-center items-center">
                        <Input
                            {...register("username")}
                            type="text"
                            id="username"
                            data-testid="login-username"
                            invalid={!!errors.username}
                            aria-label={t("form.username.ariaLabel")}
                            disabled={isLoading}
                            required
                        />

                        {errors.username && errors.username.message && (
                            <ValidationAlert message={t((errors.username.message || "errors.unknown-username-issue") as any)}/>
                        )}
                    </div>
                </section>

                <section className="grid gap-x-8 gap-y-6 sm:grid-cols-2 mt-4">
                    <div className="space-y-1">
                        <Subheading>{t("form.password.subheading")}:</Subheading>
                        <Text>{t("form.password.text")}</Text>
                    </div>
                    <div className="flex flex-col justify-center items-center">
                        <Input
                            type="password"
                            id="password"
                            data-testid="login-password"
                            invalid={!!errors.password}
                            disabled={isLoading}
                            required
                            {...register("password")}
                            aria-label={t("form.password.ariaLabel")}
                        />


                        {errors.password && errors.password.message && (
                            <ValidationAlert message={t((errors.password.message || "errors.unknown-password-issue") as any)}/>
                        )}
                    </div>
                </section>

                <section className="flex justify-between items-center mt-6">
                    <div>
                        {systemEmailConfigured && (
                            <Link
                                to="/password-reset"
                                className="text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                data-testid="forgot-password-link"
                            >
                                {t("forgotPassword.link")}
                            </Link>
                        )}
                    </div>
                    <Button
                        type="submit"
                        data-testid="login-submit"
                        aria-label={t("form.submit.ariaLabel")}
                        disabled={isLoading}
                    >
                        {isLoading ? t("form.submit.loading") : t("form.submit.label")}
                    </Button>
                </section>

            </form>
        </div>
    )
}
